import { ReactFlowProvider } from '@xyflow/react';
import clsx from 'clsx';
import React, { useState } from 'react';

import { TabItem, Tabs } from '@/components/tabs/tabs';
import { Heading } from '@/components/typography/heading';

import styles from '../../contractDetails.module.scss';
import { EntityRelationshipDiagram } from '../EntityRelationshipDiagram';
import { DataTypeDescriptionsTab } from './DataTypeDescriptionsTab';
import { ModelExamplesTab } from './ModelExamplesTab';

interface Model {
  description?: string;
  fields?: Record<string, any>;
}

interface DataModelSectionProps {
  models: Record<string, Model>;
  diagramNodes: any[];
  diagramEdges: any[];
  nodeTypes: any;
}

export type DataModelTab = 'relationships' | 'types' | 'examples';

// Testids preserved from the pre-KANP-1848 inline-button markup so any
// external Playwright fork that drives the tabs by id keeps working.
const DATA_MODEL_TABS: TabItem<DataModelTab>[] = [
  { value: 'relationships', label: 'Entity Relationships', 'data-testid': 'tab-entity-relationships' },
  { value: 'types', label: 'Data Type Descriptions', 'data-testid': 'tab-data-type-descriptions' },
  { value: 'examples', label: 'Model Examples', 'data-testid': 'tab-model-examples' }
];

export function DataModelSection( {
  models,
  diagramNodes,
  diagramEdges,
  nodeTypes
}: DataModelSectionProps ) {
  // Local state, not URL-bound — per KANP-1848 ticket: "Active tab state
  // local (don't pollute URL with this nested state)". The page-level
  // ?tab= and Access ?sub= params live higher up; nesting a third level
  // would make link-sharing fragile.
  const [ activeTab, setActiveTab ] = useState<DataModelTab>( 'relationships' );

  return (
    <div className={ clsx( 'layout__row', 'pad' ) }>
      <div className={ clsx( styles.card, styles.noBorderCard ) } data-testid='data-model-card'>
        <div className={ clsx( styles.cardHeader, styles.cardHeaderWithTabs ) }>
          <Heading as='h3' size='sm'>Data Model</Heading>
          <Tabs
            items={ DATA_MODEL_TABS }
            active={ activeTab }
            onChange={ setActiveTab }
            variant="plain"
            data-testid="data-model-tabs"
          />
        </div>
        <div className={ styles.cardBody }>
          <div className={ styles.tabContent }>
            { activeTab === 'relationships' && (
              <div className={ styles.tabPanel } data-testid='panel-entity-relationships'>
                <ReactFlowProvider>
                  <EntityRelationshipDiagram
                    nodes={ diagramNodes }
                    edges={ diagramEdges }
                    nodeTypes={ nodeTypes }
                  />
                </ReactFlowProvider>
              </div>
            ) }

            { activeTab === 'types' && (
              <DataTypeDescriptionsTab models={ models } />
            ) }

            { activeTab === 'examples' && (
              <ModelExamplesTab models={ models } />
            ) }
          </div>
        </div>
      </div>
    </div>
  );
}
